import { faApple, faDeezer, faSpotify } from '@fortawesome/free-brands-svg-icons';
import {
  faBell as faBellRegular,
  faCalendarAlt as faCalendarAltRegular,
  faCartShopping as faCartShoppingRegular,
  faCheck as faCheckRegular,
  faChevronLeft as faChevronLeftRegular,
  faChevronRight as faChevronRightRegular,
  faCircle,
  faClock as faClockRegular,
  faDeaf as faDeafRegular,
  faDotCircle,
  faEllipsisV as faEllipsisVRegular,
  faEnvelope as faEnvelopeRegular,
  faExchangeAlt as faExchangeAltRegular,
  faFileAlt as faFileAltRegular,
  faGear as faGearRegular,
  faLink as faLinkRegular,
  faMicrophone as faMicrophoneRegular,
  faMoon as faMoonRegular,
  faNavicon as faNaviconRegular,
  faPaperPlane as faPaperPlaneRegular,
  faPause as faPauseRegular,
  faPenToSquare as faPenToSquareRegular,
  faPlay as faPlayRegular,
  faPlus as faPlusRegular,
  faPodcast as faPodcastRegular,
  faRadio as faRadioRegular,
  faRedoAlt as faRedoAltRegular,
  faSearch as faSearchRegular,
  faShareFromSquare as faShareFromSquareRegular,
  faShareNodes as faShareNodesRegular,
  faStop as faStopRegular,
  faTag as faTagRegular,
  faThumbsDown as faThumbsDownRegular,
  faThumbsUp as faThumbsUpRegular,
  faUndoAlt as faUndoAltRegular,
  faUserAlt as faUserAltRegular,
  faVolumeHigh as faVolumeHighRegular,
  faXmark as faXmarkRegular,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faBell,
  faCalendarAlt,
  faCartShopping,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faClock,
  faDeaf,
  faEllipsisV,
  faEnvelope,
  faExchangeAlt,
  faFileAlt,
  faGear,
  faLink,
  faMicrophone,
  faMoon,
  faNavicon,
  faPaperPlane,
  faPause,
  faPenToSquare,
  faPlay,
  faPlus,
  faPodcast,
  faRadio,
  faRedoAlt,
  faSearch,
  faShareFromSquare,
  faShareNodes,
  faStop,
  faTag,
  faThumbsDown,
  faThumbsUp,
  faUndoAlt,
  faUserAlt,
  faVolumeHigh,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import {
  Image,
  StyleSheet,
  TouchableOpacity,
  TouchableOpacityProps,
  ViewStyle,
} from 'react-native';
import { mvs } from 'react-native-size-matters';

import { SoundQualityIcon } from './SoundQualityIcon';
import { useAppSelector } from '../app/hooks';
import { IS_IOS, IS_WEB } from '../constants';
import { selectIconStyle } from '../features/appconfig/store/selectors';

export type CustomIconName =
  | 'play_with_offset'
  | 'pause'
  | 'volume'
  | 'muted'
  | 'like'
  | 'dislike'
  | 'contact'
  | 'share_player'
  | 'share_with_offset'
  | 'shop'
  | 'hd'
  | 'sd'
  | 'switch_radio'
  | 'close'
  | 'menu'
  | 'edit_with_offset'
  | 'play_video_with_offset'
  | 'record'
  | 'stop'
  | 'listening'
  | 'abort'
  | 'search'
  | 'send'
  | 'check'
  | 'about_radio'
  | 'about_website'
  | 'about_mobile'
  | 'about_player'
  | 'plus_with_offset'
  | 'replay'
  | 'alarm'
  | 'icon-moon-outline'
  | 'icon-moon-sharp'
  | 'sort'
  | 'dot-circle'
  | 'circle'
  | 'calendar'
  | 'back'
  | 'chevron_right'
  | 'clock'
  | 'podcastMenu'
  | 'skipForward'
  | 'user'
  | 'link'
  | 'tag'
  | 'explicit'
  | 'podcast'
  | 'file'
  | 'gear'
  | 'spotify'
  | 'deezer'
  | 'apple';

export type CustomIconProps = {
  name: CustomIconName;
  size?: number;
  color?: string;
  containerStyle?: ViewStyle;
  style?: ViewStyle;
  onPress?: TouchableOpacityProps['onPress'];
  hitSlop?: TouchableOpacityProps['hitSlop'];
  disabled?: boolean;
  accessibilityLabel?: string;
  accessibilityRole?: TouchableOpacityProps['accessibilityRole'];
};

export const CustomIcon = (props: CustomIconProps) => {
  const {
    onPress,
    disabled,
    hitSlop,
    containerStyle,
    accessibilityLabel,
    accessibilityRole,
    ...otherProps
  } = props;
  const iconType = useAppSelector(selectIconStyle);

  const icon = iconMap(otherProps, iconType);

  if (!onPress) {
    return icon;
  }

  return (
    <TouchableOpacity
      style={containerStyle as ViewStyle}
      onPress={onPress}
      hitSlop={hitSlop ?? { top: mvs(5), bottom: mvs(5), left: mvs(5), right: mvs(5) }}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={accessibilityRole}
    >
      {icon}
    </TouchableOpacity>
  );
};

const iconMap = (otherProps: CustomIconProps, iconType: 'solid' | 'regular') => {
  const isSolid = iconType === 'solid';

  switch (otherProps.name) {
    case 'play_with_offset':
      return (
        <FontAwesomeIcon
          style={[otherProps.style, { marginLeft: mvs(3) }]}
          {...otherProps}
          icon={isSolid ? faPlay : faPlayRegular}
        />
      );
    case 'pause':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faPause : faPauseRegular} />
      );
    case 'like':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faThumbsUp : faThumbsUpRegular}
        />
      );
    case 'dislike':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faThumbsDown : faThumbsDownRegular}
        />
      );
    case 'contact':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faEnvelope : faEnvelopeRegular}
        />
      );
    case 'share_player':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faShareNodes : faShareNodesRegular}
        />
      );
    case 'share_with_offset':
      return IS_IOS ? (
        <FontAwesomeIcon
          style={[otherProps.style, { marginLeft: mvs(3) }]}
          {...otherProps}
          icon={isSolid ? faShareFromSquare : faShareFromSquareRegular}
        />
      ) : (
        <FontAwesomeIcon
          style={[otherProps.style, { marginRight: mvs(1) }]}
          {...otherProps}
          icon={isSolid ? faShareNodes : faShareNodesRegular}
        />
      );
    case 'shop':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faCartShopping : faCartShoppingRegular}
        />
      );
    case 'plus_with_offset':
      return (
        <FontAwesomeIcon
          {...otherProps}
          style={[otherProps.style, { marginRight: 2 }]}
          icon={isSolid ? faPlus : faPlusRegular}
        />
      );
    case 'alarm':
      return <FontAwesomeIcon {...otherProps} icon={isSolid ? faBell : faBellRegular} />;
    case 'hd':
      return <SoundQualityIcon isHd={true} />;
    case 'sd':
      return <SoundQualityIcon isHd={false} />;
    case 'switch_radio':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faRadio : faRadioRegular} />
      );
    case 'close':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faXmark : faXmarkRegular} />
      );
    case 'menu':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faNavicon : faNaviconRegular} />
      );
    case 'edit_with_offset':
      return (
        <FontAwesomeIcon
          style={[
            otherProps.style,
            { marginLeft: mvs(3), marginBottom: IS_IOS ? mvs(2) : 0 },
          ]}
          {...otherProps}
          icon={isSolid ? faPenToSquare : faPenToSquareRegular}
        />
      );
    case 'play_video_with_offset':
      return (
        <FontAwesomeIcon
          style={[otherProps.style, { marginLeft: mvs(3) }]}
          {...otherProps}
          icon={isSolid ? faPlay : faPlayRegular}
        />
      );
    case 'record':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faMicrophone : faMicrophoneRegular}
        />
      );
    case 'stop':
      return <FontAwesomeIcon {...otherProps} icon={isSolid ? faStop : faStopRegular} />;
    case 'listening':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faVolumeHigh : faVolumeHighRegular}
        />
      );
    case 'abort':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faXmark : faXmarkRegular} />
      );
    case 'send':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faPaperPlane : faPaperPlaneRegular}
        />
      );
    case 'check':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faCheck : faCheckRegular} />
      );
    case 'about_radio':
      return (
        <Image
          style={IS_WEB ? styles.aboutRadioIcon : undefined}
          source={require('../assets/icons/about-radio-icon.png')}
        />
      );
    case 'about_website':
      return (
        <Image
          style={IS_WEB ? styles.aboutWebsiteIcon : undefined}
          source={require('../assets/icons/about-website-icon.png')}
        />
      );
    case 'about_mobile':
      return (
        <Image
          style={[IS_WEB ? styles.aboutMobileIcon : undefined]}
          source={require('../assets/icons/about-mobile-icon.png')}
        />
      );
    case 'about_player':
      return (
        <Image
          style={IS_WEB ? styles.aboutPlayerIcon : undefined}
          source={require('../assets/icons/about-player-icon.png')}
        />
      );
    case 'icon-moon-outline':
      return <FontAwesomeIcon {...otherProps} icon={isSolid ? faMoon : faMoonRegular} />;
    case 'icon-moon-sharp':
      return <FontAwesomeIcon {...otherProps} icon={isSolid ? faMoon : faMoonRegular} />;
    case 'replay':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faUndoAlt : faUndoAltRegular} />
      );
    case 'skipForward':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faRedoAlt : faRedoAltRegular} />
      );
    case 'sort':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faExchangeAlt : faExchangeAltRegular}
        />
      );
    case 'dot-circle':
      return <FontAwesomeIcon {...otherProps} icon={faDotCircle} />;
    case 'circle':
      return <FontAwesomeIcon {...otherProps} icon={faCircle} />;
    case 'calendar':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faCalendarAlt : faCalendarAltRegular}
        />
      );
    case 'back':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faChevronLeft : faChevronLeftRegular}
        />
      );
    case 'chevron_right':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faChevronRight : faChevronRightRegular}
        />
      );
    case 'clock':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faClock : faClockRegular} />
      );
    case 'podcastMenu':
      return (
        <FontAwesomeIcon
          {...otherProps}
          icon={isSolid ? faEllipsisV : faEllipsisVRegular}
        />
      );
    case 'user':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faUserAlt : faUserAltRegular} />
      );
    case 'link':
      return <FontAwesomeIcon {...otherProps} icon={isSolid ? faLink : faLinkRegular} />;
    case 'tag':
      return <FontAwesomeIcon {...otherProps} icon={isSolid ? faTag : faTagRegular} />;
    case 'explicit':
      return <FontAwesomeIcon {...otherProps} icon={isSolid ? faDeaf : faDeafRegular} />;
    case 'podcast':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faPodcast : faPodcastRegular} />
      );
    case 'file':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faFileAlt : faFileAltRegular} />
      );
    case 'gear':
      return <FontAwesomeIcon {...otherProps} icon={isSolid ? faGear : faGearRegular} />;
    case 'search':
      return (
        <FontAwesomeIcon {...otherProps} icon={isSolid ? faSearch : faSearchRegular} />
      );
    case 'spotify':
      return <FontAwesomeIcon {...otherProps} icon={faSpotify} />;
    case 'deezer':
      return <FontAwesomeIcon {...otherProps} icon={faDeezer} />;
    case 'apple':
      return <FontAwesomeIcon {...otherProps} icon={faApple} />;
  }
  return null;
};

const styles = StyleSheet.create({
  aboutRadioIcon: {
    width: 25,
    height: 25,
  },
  aboutWebsiteIcon: {
    width: 25,
    height: 23,
  },
  aboutMobileIcon: {
    width: 16,
    height: 25,
  },
  aboutPlayerIcon: {
    width: 20,
    height: 25,
  },
});
